import { useCallback, useEffect, useRef, useState } from 'react';
import AudioPlayerComponent from './AudioPlayer.component';

export type AudioPlayerProps = {
  audioUrl: string;
  extraId?: string;
  onDelete?: () => void;
};

const ONE_SECOND_IN_MS = 1000;
const A_BIG_TIME_IN_MS = 10_000_000;

export default function AudioPlayer({
  audioUrl,
  onDelete,
  extraId = '',
}: AudioPlayerProps): JSX.Element {
  const audio = useRef<any>(null);
  const interval = useRef<any>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [totalDuration, setTotalDuration] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);

  const audioPlayerId = `${extraId}-audio`;

  const setTotalTimeFromAudio = useCallback(() => {
    setTotalDuration(audio.current.duration);
    setRemainingTime(audio.current.duration);

    audio.current.currentTime = 0;
    audio.current.removeEventListener('timeupdate', setTotalTimeFromAudio);
  }, []);

  useEffect(() => {
    const setInitialTime = (): void => {
      if (audio.current.duration === Infinity) {
        audio.current.currentTime = A_BIG_TIME_IN_MS;
        audio.current.addEventListener('timeupdate', setTotalTimeFromAudio);
      } else {
        setTotalTimeFromAudio();
      }
    };

    if (!audioUrl) {
      setRemainingTime(0);
      setTotalDuration(0);
      setIsPlaying(false);
      interval.current = null;
      audio.current = null;

      return () => {};
    }

    audio.current = document.getElementById(audioPlayerId);
    audio.current.src = audioUrl;

    audio.current.addEventListener('loadedmetadata', setInitialTime);

    return () => {
      audio.current.removeEventListener('loadedmetadata', setInitialTime);
    };
  }, [audioPlayerId, audioUrl, setTotalTimeFromAudio]);

  useEffect(() => {
    if (isPlaying) {
      audio.current.play();
    } else {
      audio.current.pause();
    }
  }, [audio, isPlaying]);

  const onPause = (): void => {
    setIsPlaying(false);
    clearInterval(interval.current);
    interval.current = null;
  };

  const onRestart = (): void => {
    setRemainingTime(totalDuration);
  };

  const onPlay = (): void => {
    setIsPlaying(true);

    const newInterval = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime > 1) {
          return prevTime - 1;
        }

        onPause();
        onRestart();
        return 0;
      });
    }, ONE_SECOND_IN_MS);

    interval.current = newInterval;
  };

  const onToggleDeleteModal = (event?: any): void => {
    event?.stopPropagation();
    setIsDeleteModalOpen((prev) => !prev);
  };

  const handleDelete = (): void => {
    onToggleDeleteModal();

    if (onDelete) {
      onDelete();
    }
  };

  return (
    <AudioPlayerComponent
      remainingTime={remainingTime}
      isPlaying={isPlaying}
      onPlay={onPlay}
      onPause={onPause}
      isDeleteModalOpen={isDeleteModalOpen}
      onToggleDeleteModal={onToggleDeleteModal}
      onDelete={handleDelete}
      canDelete={!!onDelete}
      audioPlayerId={audioPlayerId}
    />
  );
}
